import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Check, DollarSign, TrendingUp, Users, Award,
  Headphones, BookOpen, Rocket, Shield, Gift, Star,
} from "lucide-react";
import Testimonials from "../../components/Testimonials";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATS = [
  { icon: <DollarSign size={22} />, val: "45%", label: "Average Margin", bg: "bg-[#FFF9E6]" },
  { icon: <TrendingUp size={22} />, val: "100+", label: "Active Resellers", bg: "bg-[#E8F5E9]" },
  { icon: <Users size={22} />, val: "1,000+", label: "Businesses Served", bg: "bg-[#FFE4E8]" },
  { icon: <Award size={22} />, val: "4.9/5", label: "Partner Rating", bg: "bg-[#FFF3D6]" },
];

const WHY = [
  { icon: <DollarSign size={20} />, t: "Attractive Margins", d: "Earn up to 45% margin on every sale with volume-based incentives and performance bonuses.", bg: "bg-[#FFF9E6]" },
  { icon: <Headphones size={20} />, t: "Dedicated Support", d: "Get priority support with a dedicated partner success manager and 24/7 technical assistance.", bg: "bg-[#E8F5E9]" },
  { icon: <BookOpen size={20} />, t: "Training & Resources", d: "Access comprehensive training materials, sales kits, and marketing resources to succeed.", bg: "bg-[#FFE4E8]" },
  { icon: <Rocket size={20} />, t: "Fast Onboarding", d: "Get up and running quickly with our streamlined partner onboarding process.", bg: "bg-[#FFF3D6]" },
  { icon: <Shield size={20} />, t: "Protected Territory", d: "Exclusive territory rights ensure you have a competitive advantage in your region.", bg: "bg-[#E8F5E9]" },
  { icon: <Gift size={20} />, t: "Marketing Support", d: "Co-marketing opportunities, lead generation support, and pre-built campaigns.", bg: "bg-[#FFE4E8]" },
];

const TIERS = [
  {
    name: "Silver Partner", range: "0–10 deals/year", margin: "20%",
    perks: ["Basic training", "Email support", "Marketing materials", "Partner portal access"],
    popular: false,
  },
  {
    name: "Gold Partner", range: "11–25 deals/year", margin: "30%",
    perks: ["Advanced training", "Priority support", "Co-marketing opportunities", "Quarterly business reviews"],
    popular: true,
  },
  {
    name: "Platinum Partner", range: "26+ deals/year", margin: "45%",
    perks: ["Dedicated success manager", "24/7 priority support", "Exclusive territory rights", "Custom integrations support"],
    popular: false,
  },
];

const STORIES = [
  { n: "Michael Benson", c: "EduTech Solutions", q: "Partnering with Brivo has transformed our business. We've grown from 5 to 50 clients in just 18 months.", stat: "10x revenue growth", img: "https://images.unsplash.com/photo-1765648636178-60e73bcc865e?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" },
  { n: "David Martins", c: "Nordic Systems", q: "The support and training from Brivo is exceptional. We feel like true partners, not just resellers.", stat: "30+ deals onboarded", img: "https://images.unsplash.com/photo-1758887261865-a2b89c0f7ac5?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" },
  { n: "Sarah Johnson", c: "Atlas Infrastructure", q: "The margins are competitive and the product sells itself. Our clients love Brivo.", stat: "₦5M+ annual revenue", img: "https://images.unsplash.com/photo-1769636930047-4478f12cf430?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" },
];

export default function ResellerProgram() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
  
    if (!form.name || !form.email.includes("@") || !form.company || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
  
    setLoading(true);

try {
  await emailjs.send(
    "YOUR_SERVICE_ID",
    "YOUR_TEMPLATE_ID",
    {
      name: form.name,
      email: form.email,
      company: form.company,
      message: form.message,
    },
    "YOUR_PUBLIC_KEY"
  );

  toast.success("Application received! We'll respond within 48 hours.");
  setForm({ name: "", email: "", company: "", message: "" });

} catch (err) {
  console.log(err); // 👈 helps debug
  toast.error("Submission failed. Try again.");
} finally {
  setLoading(false);
}
  };

  return (
    <div data-testid="reseller-program-page">
      {/* HERO with stats grid */}
      <section className="relative overflow-hidden hero-gradient grain">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#FFE4E8] opacity-50 blur-3xl animate-blob" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#E8F5E9] opacity-60 blur-3xl animate-blob" style={{ animationDelay: "3s" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-up">
            <span className="inline-block px-4 py-1.5 rounded-full glass-strong border border-[#12b571]/10 text-black text-xs font-bold uppercase tracking-widest mb-6">
              Reseller Program
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.02]">
              Partner with Brivo.<br />
              <span className="italic text-black/60 font-medium">Grow together.</span>
            </h1>
            <p className="mt-7 text-lg text-black/75 leading-relaxed max-w-lg">
              Join our reseller program and help businesses transform their operations while building a profitable business of your own. Get exclusive pricing, dedicated support, and powerful sales tools.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a href="#apply" className="btn-primary">Apply Now <ArrowRight size={18} /></a>
              <Link to="/support" className="btn-secondary">Contact Us</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 animate-fade-up delay-200">
            {STATS.map((s) => (
              <div key={s.label} className={`${s.bg} rounded-3xl p-6 border border-[#12b571]/8 card-hover`}>
                <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center text-black mb-4">
                  {s.icon}
                </div>
                <div className="font-display font-extrabold text-4xl text-black tracking-tight">{s.val}</div>
                <div className="text-sm text-black/60 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PARTNER */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#FFF9E6] text-black text-xs font-semibold mb-4">
              Why us
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight leading-[1.05]">
              Why Partner with Brivo?
            </h2>
            <p className="mt-5 text-lg text-black/70">
              Six reasons our top resellers won't sell anything else.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map((w) => (
              <div key={w.t} className="bg-[#FFFCF2] rounded-3xl p-7 border border-[#12b571]/8 card-hover">
                <div className={`w-14 h-14 rounded-2xl ${w.bg} flex items-center justify-center text-black mb-5`}>
                  {w.icon}
                </div>
                <h3 className="font-display font-bold text-xl text-black mb-3">{w.t}</h3>
                <p className="text-black/70 leading-relaxed text-sm">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER TIERS - LIGHT */}
      <section className="py-24 md:py-32 bg-[#FFE4E8] relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/70 backdrop-blur text-black text-xs font-semibold mb-4">
              Partner Tiers
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
              Grow your business.<br />
              <span className="italic text-black/60 font-medium">Unlock higher margins.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {TIERS.map((t) => (
              <div
                key={t.name}
                data-testid={`tier-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`rounded-3xl p-8 transition-all relative ${
                  t.popular
                    ? "bg-[#12b571] text-white shadow-2xl scale-[1.04] border-2 border-[#12b571]"
                    : "bg-white border border-[#12b571]/8 card-hover"
                }`}
              >
                {t.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#FFF9E6] text-black text-xs font-bold shadow-md">
                    <Star size={12} className="fill-black" /> Most Popular
                  </div>
                )}
                <h3 className={`font-display font-bold text-2xl ${t.popular ? "text-white" : "text-black"}`}>{t.name}</h3>
                <p className={`text-sm mt-1 ${t.popular ? "text-white/70" : "text-black/60"}`}>{t.range}</p>
                <div className={`mt-6 font-display font-extrabold text-6xl tracking-tight ${t.popular ? "text-white" : "text-black"}`}>
                  {t.margin}
                  <span className={`text-base font-medium block mt-1 ${t.popular ? "text-white/60" : "text-black/50"}`}>margin</span>
                </div>
                <ul className="mt-7 space-y-3">
                  {t.perks.map((p) => (
                    <li key={p} className={`flex items-start gap-2 text-sm ${t.popular ? "text-white/90" : "text-black/80"}`}>
                      <Check size={16} className={`mt-0.5 flex-shrink-0 ${t.popular ? "text-[#FFF9E6]" : "text-black"}`} /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUCCESS STORIES */}
      {/* <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#E8F5E9] text-black text-xs font-semibold mb-4">
              Success stories
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
              Real partners.<br /><span className="italic text-black/60 font-medium">Real growth.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {STORIES.map((s, i) => (
              <div key={i} className="bg-[#FFFCF2] rounded-3xl p-7 border border-[#12b571]/8 card-hover">
                <div className="flex items-center gap-3 mb-5">
                  <img src={s.img} alt={s.n} className="w-14 h-14 rounded-full object-cover border-2 border-white shadow" />
                  <div>
                    <div className="font-semibold text-black">{s.n}</div>
                    <div className="text-xs text-black/60">{s.c}</div>
                  </div>
                </div>
                <p className="text-black/85 leading-relaxed italic mb-6">"{s.q}"</p>
                <div className="flex items-center gap-2 pt-5 border-t border-[#12b571]/10 text-sm font-semibold text-black">
                  <TrendingUp size={16} /> {s.stat}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      <Testimonials />

      {/* APPLY FORM */}
      <section id="apply" className="py-24 md:py-32 bg-[#FFF9E6] relative overflow-hidden">
        <div className="absolute -top-32 left-1/4 w-96 h-96 rounded-full bg-white/40 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
              Apply to become a reseller
            </h2>
            <p className="mt-4 text-lg text-black/70">If you are having issue with the form, eamil your information to <b>reseller@brivo360.com</b>.</p>
          </div>
          <form onSubmit={onSubmit} className="bg-white rounded-3xl p-8 md:p-10 border border-[#12b571]/8 shadow-xl space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold text-black mb-2 block">Full name *</label>
                <input
                  data-testid="reseller-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
                  placeholder="Adunni Ola"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-black mb-2 block">Email *</label>
                <input
                  data-testid="reseller-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-black mb-2 block">Company name *</label>
              <input
                data-testid="reseller-company"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2]"
                placeholder="Your Company"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-black mb-2 block">Why do you want to become a reseller? *</label>
              <textarea
                data-testid="reseller-message"
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-[#12b571]/15 focus:border-[#12b571] focus:outline-none transition-colors bg-[#FFFCF2] resize-none"
                placeholder="Tell us about your interest, experience, and territory..."
              />
            </div>
            <button data-testid="reseller-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60">
              {loading ? "Submitting..." : "Submit Application"} <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
